import React, { useCallback, useEffect, useState } from "react";
import { useGetLLMPassThroughMutation } from "../../../store/api/lumi/llm";
import { useParams } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { useGetSentimentsDocsMutation } from "../../../store/api/lumi/sentiment";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import SummaryText from "../../Summary/SummaryText";
import SummaryDocuments from "../../Summary/SummaryDocuments";
import { Collapse, Pagination, Tooltip } from "antd";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import LoadingIndicator from "../../Summary/LoadingIndicator";
import Scrollbars from "react-custom-scrollbars-2";
import {Typography } from 'antd';

const { Text} = Typography;
const CopyToClipboard = ({ content }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(content);
        setCopied(true);
        // message.success("Copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
      };

    return (
        <Tooltip title={copied ? "Copied!" : "Copy"}>
          {copied ? <CheckOutlined style={{ color: "green" }} /> : <CopyOutlined onClick={handleCopy} style={{ cursor: "pointer" }} />}
        </Tooltip>
    );

}

const NewSummaryText = ({ content, loading }) => {
    if (loading) {
        return <LoadingIndicator />
    }
    return (
        <p style={{ maxHeight:"150px", overflow:"auto", color:"#353935", lineHeight:"19px", margin:"0", padding:"0 5px"}}>{content || ""}</p>
    )
}

const NewSummaryDocuments = ({ loading, documents }) => {
    const [documentsIndex, setDocumentsIndex] = useState(1);
    if (loading) {
        return <LoadingIndicator />;
    }

    return (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <Scrollbars style={{ height: "180px" }}>
                {documents && documents.length > 0 ? (
                    <p style={{ maxHeight:"150px", overflow:"auto", color:"#353935", lineHeight:"19px", margin:"0", padding:"0 5px" }}>
                        {documents[documentsIndex - 1].text}
                    </p>
                ) : (
                    <p>no documents found</p>
                )}
            </Scrollbars>
            <div style={{textAlign:"center"}}>
            <Pagination
                simple={{
                    readOnly: true,
                }}
                defaultCurrent={2}
                current={documentsIndex}
                total={documents.length}
                onChange={(page) => setDocumentsIndex(page)}
                showSizeChanger={false}
                pageSize={1}
            />
            </div>
        </div>
    );


}

const SummaryForSentiment = ({ concept, additionalFilter, props }) => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isNewApp = searchParams.get("app") === "new";

    const [triggerGetDocuments] = useGetSentimentsDocsMutation();
    const [triggerGetLLMPassThrough] = useGetLLMPassThroughMutation();

    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [loadingSummaries, setLoadingSummaries] = useState(false);

    const [negativeSummary, setNegativeSummary] = useState(null);
    const [positiveSummary, setPositiveSummary] = useState(null);

    const [negativeSummaryError, setNegativeSummaryError] = useState(null);
    const [positiveSummaryError, setPositiveSummaryError] = useState(null);

    const [negativeDocuments, setNegativeDocuments] = useState([]);
    const [positiveDocuments, setPositiveDocuments] = useState([]);


    const [fetchedSummaries, setFetchedSummaries] = useState({});
    const handleLlmRequest = useCallback(
        async (sentimentType, documents) => {
            setLoadingDocuments(true);
            if (!documents.length) return null;

            const maxTokens = 150000;
            let promptLlm;
            if (concept.shared_concept_id) {
                promptLlm =`summarize the documents focusing on the ${sentimentType} statements related to any of the following concepts: "${concept.texts.join(", ")}" in the in these documents\n` +
                    documents.map((d) => `<p>${d.text}</p>`).join("");
            } else {
                promptLlm = `Summarize how "${concept.name}" is spoken about ${sentimentType}ly in these documents\n` +
                    documents.map((d) => `<p>${d.text}</p>`).join("");
            }
            if (promptLlm.length > maxTokens) {
                promptLlm = promptLlm.slice(0, maxTokens) + "...";
            }
            try {
                const llmResponse = await triggerGetLLMPassThrough({
                    projectId: id,
                    prompt: promptLlm,
                }).unwrap();
                return llmResponse.response;
            } catch (error) {
                if (sentimentType === "negative") {
                    setNegativeSummaryError(error.message || "An internal error occurred; contact Luminoso for assistance.");
                } else {
                    setPositiveSummaryError(error.message || "An internal error occurred; contact Luminoso for assistance.");
                }
                return null;
            }
        },
        [concept.name],
    );

    const fetchDocuments = useCallback(async () => {
        setLoadingDocuments(true);
        const conceptFilter = getConceptFilter(props);
        try {

            const result = await triggerGetDocuments({
                id,
                filter: additionalFilter || [],
                search: concept.texts,
                concept_selector: conceptFilter,
                include_sentiment_on_concepts: true,
            }).unwrap();

            const positiveDocuments1 = result.filter(
                (doc) => doc.sentiment === "positive",
            );
            const negativeDocuments1 = result.filter(
                (doc) => doc.sentiment === "negative",
            );

            setPositiveDocuments(positiveDocuments1);
            setNegativeDocuments(negativeDocuments1);

            await fetchSummaries(positiveDocuments1, negativeDocuments1);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingDocuments(false);
        }
    }, [additionalFilter, concept.name]);


    const fetchSummaries = async (positiveDocuments, negativeDocuments) => {
        if (fetchedSummaries[concept.name]) {
            const { negative, positive } = fetchedSummaries[concept.name];
            setNegativeSummary(negative);
            setPositiveSummary(positive);
            return;
        }

        setLoadingSummaries(true);
        try {
            const [negative, positive] = await Promise.all([
                handleLlmRequest("negative", negativeDocuments),
                handleLlmRequest("positive", positiveDocuments),
            ]);

            setNegativeSummary(negative);
            setPositiveSummary(positive);

            setFetchedSummaries((prev) => ({
                ...prev,
                [concept.name]: { negative, positive },
            }));
        } catch (error) {
            console.error("Failed to fetch summaries:", error);
        } finally {
            setLoadingSummaries(false);
        }
    };

    useEffect(() => {
        if (concept.name) {
            fetchDocuments();
        }
    }, [concept.name]);
  
    if (isNewApp) {
        const items = [
            {
                key: "1",
                label: <Text strong style={{fontSize: "16px", color:"#090A09", fontWeight:"700"}}>Summary of negative mentions</Text>,
                children: (
                    <NewSummaryText
                    header="Summary of negative mentions"
                    loading={loadingSummaries || loadingDocuments}
                    summary={negativeSummary}
                    error={negativeSummaryError}
                    />
                ),
                extra: <CopyToClipboard content={negativeSummary} />,
            },
            {
                key: "2",
                label: <Text strong style={{fontSize: "16px", color:"#090A09", fontWeight:"700"}}>Sample of negative mention documents</Text>,
                children: (
                    <NewSummaryDocuments
                    header="Sample of negative mention documents"
                    loading={loadingDocuments}
                    documents={negativeDocuments}
                    />
                ),
            },
            {
                key: "3",
                label: <Text strong style={{fontSize: "16px", color:"#090A09", fontWeight:"700"}}>Summary of positive mentions</Text>,
                children: (
                    <NewSummaryText
                    header="Summary of positive mentions"
                    loading={loadingSummaries || loadingDocuments}
                    summary={positiveSummary}
                    error={positiveSummaryError}
                    />
                ),
                extra: <CopyToClipboard content={positiveSummary} />,
            },
            {
                key: "4",
                label: <Text strong style={{fontSize: "16px", color:"#090A09", fontWeight:"700"}}>Sample of positive mention documents</Text>,
                children: (
                    <NewSummaryDocuments
                        loading={loadingDocuments}
                        documents={positiveDocuments}
                    />
                ),
            },
        ];

        return <Collapse accordion ghost items={items} defaultActiveKey={["2"]}/>;
    }    
   
    return (
        <>
            <SummaryText
                header="Summary of negative mentions"
                loading={loadingSummaries || loadingDocuments}
                summary={negativeSummary}
                error={negativeSummaryError}
            />
            <SummaryDocuments
                header="Sample of negative mention documents"
                loading={loadingDocuments}
                documents={negativeDocuments}
            />
            <SummaryText
                header="Summary of positive mentions"
                loading={loadingSummaries || loadingDocuments}
                summary={positiveSummary}
                error={positiveSummaryError}
            />
            <SummaryDocuments
                header="Sample of positive mention documents"
                loading={loadingDocuments}
                documents={positiveDocuments}
            />
        </>
    );
};

export default SummaryForSentiment;
